import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, 
  Download, 
  Calendar,
  User,
  Building2,
  CreditCard,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContractGenerationPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: اختيار القالب، 2: تعبئة البيانات، 3: معاينة
  
  // القوالب
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // العملاء
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // بيانات العقد
  const [formData, setFormData] = useState({
    // بيانات العميل
    client_name: '',
    client_nationality: 'سعودي',
    client_national_id: '',
    client_id_number: '',
    client_cr_number: '',
    client_address: '',
    client_city: 'جدة',
    client_district: '',
    client_postal_code: '',
    client_phone: '',
    client_email: '',
    client_representation_type: 'أصالة عن نفسه',
    client_authorization_number: '',
    
    // بيانات العقد
    contract_date: new Date().toISOString().split('T')[0],
    contract_date_hijri: '',
    contract_date_gregorian: new Date().toISOString().split('T')[0],
    contract_duration: '',
    service_value: '',
    service_description: '',
    case_description: '',
    language: 'bilingual',
    
    // بيانات الأتعاب
    total_fees_amount: '',
    total_fees_words: '',
    study_fees_amount: '',
    study_fees_words: '',
    first_payment: '',
    first_payment_amount: '',
    first_payment_words: '',
    second_payment: '',
    second_payment_amount: '',
    second_payment_words: '',
    third_payment: '',
    payment_total: '',
    
    // بيانات المحامي والشركة
    lawyer_name: 'أ. عبدالرحمن محمد',
    lawyer_national_id: '1234567890',
    lawyer_phone: '0501234567',
    lawyer_email: 'lawyer@wazin.law',
    law_firm_name: 'شركة وازن للمحاماة والاستشارات القانونية',
    law_firm_cr_number: '1010000000',
    
    // إعدادات
    case_id: null,
    notes: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedContract, setGeneratedContract] = useState(null);

  // جلب القوالب
  useEffect(() => {
    fetchTemplates();
    fetchClients();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/contracts/templates?is_active=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (err) {
      console.error('خطأ في جلب القوالب:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/users/list?role=client`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data?.users || []);
    } catch (err) {
      console.error('خطأ في جلب العملاء:', err);
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setFormData({
      ...formData,
      client_name: client.full_name,
      client_national_id: client.national_id || '',
      client_phone: client.phone || '',
      client_email: client.email,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerateContract = async () => {
    if (!selectedTemplate) {
      setError('يرجى اختيار القالب');
      return;
    }
    
    if (!selectedClient && !formData.client_name) {
      setError('يرجى إدخال بيانات العميل أو اختيار عميل من القائمة');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      const requestData = {
        template_id: selectedTemplate.id,
        client_id: selectedClient?.id || null,
        case_id: formData.case_id || null,
        language: formData.language || "bilingual",
        field_data: {
          // بيانات العميل - القالب الجديد
          client_full_name: formData.client_name || "عميل تجريبي",
          client_nationality: formData.client_nationality || "سعودي",
          client_national_id: formData.client_national_id || formData.client_id_number || "0000000000",
          client_city: formData.client_city || "جدة",
          client_district: formData.client_district || "الروضة",
          client_postal_code: formData.client_postal_code || "23432",
          client_phone: formData.client_phone || "0500000000",
          client_email: formData.client_email || "client@example.com",
          client_representation_type: formData.client_representation_type || "أصالة عن نفسه",
          client_authorization_number: formData.client_authorization_number || "",
          
          // التواريخ
          contract_date_hijri: formData.contract_date_hijri || "1/1/1446هـ",
          contract_date_gregorian: formData.contract_date_gregorian || formData.contract_date || new Date().toISOString().split('T')[0],
          
          // وصف القضية
          case_description: formData.case_description || formData.service_description || "قضية قانونية",
          
          // الأتعاب
          total_fees_amount: formData.payment_total || formData.service_value || "10000",
          total_fees_words: formData.total_fees_words || "عشرة آلاف",
          study_fees_amount: formData.study_fees_amount || "1000",
          study_fees_words: formData.study_fees_words || "ألف",
          first_payment_amount: formData.first_payment || "5000",
          first_payment_words: formData.first_payment_words || "خمسة آلاف",
          second_payment_amount: formData.second_payment || "4000",
          second_payment_words: formData.second_payment_words || "أربعة آلاف",
          
          // حقول قديمة للتوافق
          client_name: formData.client_name,
          client_address: formData.client_address,
          contract_date: formData.contract_date,
          service_value: formData.service_value,
          service_description: formData.service_description
        },
        notes: formData.notes || ""
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/contracts/generate`,
        requestData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setGeneratedContract(response.data);
      setStep(3); // الانتقال إلى المعاينة
    } catch (err) {
      console.error('خطأ في توليد العقد:', err);
      setError(err.response?.data?.detail || 'حدث خطأ في توليد العقد');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async (language) => {
    if (!generatedContract) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/contracts/download/${generatedContract.id}/${language}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${generatedContract.contract_number}_${language.toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('خطأ في تحميل PDF:', err);
      alert('حدث خطأ في تحميل الملف');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-[#C8A870] to-[#b8986a] p-3 rounded-lg">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1e3a8a]">توليد عقد جديد</h1>
                <p className="text-gray-600">إنشاء عقود احترافية تلقائياً</p>
              </div>
            </div>
            
            {/* Progress Indicator */}
            <div className="flex items-center gap-2">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step >= 1 ? 'bg-[#C8A870] text-white' : 'bg-gray-200 text-gray-600'}`}>
                <span className="font-bold">1</span>
                <span>اختيار القالب</span>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400" />
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step >= 2 ? 'bg-[#C8A870] text-white' : 'bg-gray-200 text-gray-600'}`}>
                <span className="font-bold">2</span>
                <span>تعبئة البيانات</span>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400" />
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step >= 3 ? 'bg-[#C8A870] text-white' : 'bg-gray-200 text-gray-600'}`}>
                <span className="font-bold">3</span>
                <span>المعاينة والتحميل</span>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border-r-4 border-red-500 p-4 mb-6 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800">خطأ</p>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Step 1: اختيار القالب */}
        {step === 1 && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">اختر قالب العقد</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map(template => (
                <div
                  key={template.id}
                  onClick={() => {
                    setSelectedTemplate(template);
                    setStep(2);
                  }}
                  className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                    selectedTemplate?.id === template.id
                      ? 'border-[#C8A870] bg-[#C8A870]/5'
                      : 'border-gray-200 hover:border-[#C8A870] hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <FileText className="w-6 h-6 text-[#C8A870]" />
                      <h3 className="font-bold text-gray-900">{template.name_ar}</h3>
                    </div>
                    {selectedTemplate?.id === template.id && (
                      <CheckCircle className="w-6 h-6 text-[#C8A870]" />
                    )}
                  </div>
                  {template.description_ar && (
                    <p className="text-gray-600 text-sm">{template.description_ar}</p>
                  )}
                </div>
              ))}
            </div>

            {templates.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>لا توجد قوالب متاحة حالياً</p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: تعبئة البيانات */}
        {step === 2 && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">تعبئة بيانات العقد</h2>
              <button
                onClick={() => setStep(1)}
                className="text-gray-600 hover:text-gray-900"
              >
                ← تغيير القالب
              </button>
            </div>

            <div className="space-y-6">
              {/* اختيار العميل */}
              <div className="bg-blue-50 rounded-lg p-4">
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  <User className="w-4 h-4 inline ml-2" />
                  اختر العميل (اختياري)
                </label>
                <select
                  onChange={(e) => {
                    const client = clients.find(c => c.id === e.target.value);
                    if (client) handleClientSelect(client);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870] focus:border-transparent"
                >
                  <option value="">-- اختر عميل أو أدخل البيانات يدوياً --</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.full_name} - {client.email}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-600 mt-2">
                  💡 يمكنك تعبئة البيانات يدوياً في الحقول أدناه بدون اختيار عميل من القائمة
                </p>
              </div>

              {/* بيانات العميل */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسم العميل الكامل *
                  </label>
                  <input
                    type="text"
                    name="client_name"
                    value={formData.client_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    رقم الهوية الوطنية *
                  </label>
                  <input
                    type="text"
                    name="client_national_id"
                    value={formData.client_national_id}
                    onChange={handleInputChange}
                    placeholder="مثال: 1234567890"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    رقم الجوال *
                  </label>
                  <input
                    type="text"
                    name="client_phone"
                    value={formData.client_phone}
                    onChange={handleInputChange}
                    placeholder="مثال: 0501234567"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    البريد الإلكتروني
                  </label>
                  <input
                    type="email"
                    name="client_email"
                    value={formData.client_email}
                    onChange={handleInputChange}
                    placeholder="client@example.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    المدينة
                  </label>
                  <input
                    type="text"
                    name="client_city"
                    value={formData.client_city}
                    onChange={handleInputChange}
                    placeholder="مثال: جدة"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    العنوان
                  </label>
                  <input
                    type="text"
                    name="client_address"
                    value={formData.client_address}
                    onChange={handleInputChange}
                    placeholder="مثال: حي الرويس"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الرمز البريدي
                  </label>
                  <input
                    type="text"
                    name="client_postal_code"
                    value={formData.client_postal_code}
                    onChange={handleInputChange}
                    placeholder="مثال: 23442"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                  />
                </div>
              </div>

              {/* بيانات العقد */}
              <div className="border-t pt-6">
                <h3 className="font-bold text-gray-900 mb-4">بيانات العقد</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="w-4 h-4 inline ml-1" />
                      تاريخ العقد *
                    </label>
                    <input
                      type="date"
                      name="contract_date"
                      value={formData.contract_date}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="w-4 h-4 inline ml-1" />
                      مدة العقد
                    </label>
                    <input
                      type="text"
                      name="contract_duration"
                      value={formData.contract_duration}
                      onChange={handleInputChange}
                      placeholder="مثال: سنة واحدة"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      وصف القضية / موضوع العقد *
                    </label>
                    <textarea
                      name="case_description"
                      value={formData.case_description}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="مثال: دعوى الماجستير غير المعتمد..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <CreditCard className="w-4 h-4 inline ml-1" />
                      رسوم دراسة المعاملة
                    </label>
                    <input
                      type="text"
                      name="first_payment"
                      value={formData.first_payment}
                      onChange={handleInputChange}
                      placeholder="مثال: 5,000"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الدفعة الأولى
                    </label>
                    <input
                      type="text"
                      name="second_payment"
                      value={formData.second_payment}
                      onChange={handleInputChange}
                      placeholder="مثال: 10,000"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الدفعة الثانية
                    </label>
                    <input
                      type="text"
                      name="third_payment"
                      value={formData.third_payment}
                      onChange={handleInputChange}
                      placeholder="مثال: 15,000"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      إجمالي الأتعاب *
                    </label>
                    <input
                      type="text"
                      name="payment_total"
                      value={formData.payment_total}
                      onChange={handleInputChange}
                      placeholder="مثال: 30,000"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      اللغة
                    </label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870]"
                    >
                      <option value="arabic">عربي فقط</option>
                      <option value="english">إنجليزي فقط</option>
                      <option value="bilingual">ثنائي اللغة</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* أزرار */}
              <div className="flex gap-4 pt-6 border-t">
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  رجوع
                </button>
                <button
                  onClick={handleGenerateContract}
                  disabled={loading || (!selectedClient && !formData.client_name)}
                  className="flex-1 bg-gradient-to-r from-[#C8A870] to-[#b8986a] text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'جاري التوليد...' : 'توليد العقد'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: معاينة وتحميل */}
        {step === 3 && generatedContract && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-center mb-8">
              <div className="inline-block bg-green-100 rounded-full p-4 mb-4">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">تم توليد العقد بنجاح!</h2>
              <p className="text-gray-600">رقم العقد: <span className="font-bold text-[#C8A870]">{generatedContract.contract_number}</span></p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {/* PDF عربي */}
              {(generatedContract.language === 'arabic' || generatedContract.language === 'bilingual') && (
                <button
                  onClick={() => downloadPDF('ar')}
                  className="flex items-center justify-center gap-3 bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white px-6 py-4 rounded-lg font-bold hover:shadow-lg"
                >
                  <Download className="w-5 h-5" />
                  تحميل النسخة العربية
                </button>
              )}

              {/* PDF إنجليزي */}
              {(generatedContract.language === 'english' || generatedContract.language === 'bilingual') && (
                <button
                  onClick={() => downloadPDF('en')}
                  className="flex items-center justify-center gap-3 bg-gradient-to-r from-[#C8A870] to-[#b8986a] text-white px-6 py-4 rounded-lg font-bold hover:shadow-lg"
                >
                  <Download className="w-5 h-5" />
                  تحميل النسخة الإنجليزية
                </button>
              )}
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => navigate('/admin/contracts')}
                className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-200"
              >
                الذهاب إلى قائمة العقود
              </button>
              <button
                onClick={() => {
                  setStep(1);
                  setSelectedTemplate(null);
                  setSelectedClient(null);
                  setGeneratedContract(null);
                }}
                className="flex-1 border-2 border-[#C8A870] text-[#C8A870] px-6 py-3 rounded-lg font-bold hover:bg-[#C8A870]/10"
              >
                إنشاء عقد جديد
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractGenerationPage;
